import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Wind, Sparkles, Moon, Brain, Heart, Flame, ChevronRight } from 'lucide-react';
import { MicroInteraction } from '@/components/ui/MicroInteraction';

interface FeatureCard {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  gradient: string;
  path: string;
  tag?: string;
}

const FEATURED_CARDS: FeatureCard[] = [
  {
    id: 'breathwork',
    title: 'Breathwork',
    subtitle: 'Calm your mind in 5 minutes',
    icon: Wind,
    gradient: 'from-secondary to-secondary/80',
    path: '/breathwork',
    tag: 'Popular',
  },
  {
    id: 'therapy',
    title: 'AI Therapist',
    subtitle: 'Talk through anything',
    icon: Brain,
    gradient: 'from-primary to-primary/80',
    path: '/therapy',
  },
  {
    id: 'manifestation',
    title: 'Manifestation',
    subtitle: 'Visualise your future',
    icon: Sparkles,
    gradient: 'from-accent to-accent/80',
    path: '/manifestation',
  },
  {
    id: 'sleep',
    title: 'Sleep',
    subtitle: 'Wind down for better rest',
    icon: Moon,
    gradient: 'from-muted to-muted/80',
    path: '/sleep-and-dreams',
  },
];

export function FeatureSpotlight() {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Quick Access</h2>
        <button className="text-sm text-muted-foreground hover:text-primary transition-colors">
          See all
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {FEATURED_CARDS.map((card, index) => (
          <MicroInteraction key={card.id} type="press">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => navigate(card.path)}
              className={`relative w-full h-[140px] p-4 rounded-2xl bg-gradient-to-br ${card.gradient} text-white text-left overflow-hidden group`}
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-24 h-24 opacity-20">
                <card.icon className="w-full h-full" />
              </div>
              
              {/* Tag */}
              {card.tag && (
                <span className="absolute top-2 right-2 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
                  {card.tag}
                </span>
              )}
              
              {/* Content */}
              <div className="relative z-10">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                  <card.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-base mb-1">{card.title}</h3>
                <p className="text-xs text-white/80">{card.subtitle}</p>
              </div>
              
              {/* Hover arrow */}
              <motion.div
                initial={{ x: -5, opacity: 0 }}
                whileHover={{ x: 0, opacity: 1 }}
                className="absolute bottom-4 right-4"
              >
                <ChevronRight className="w-5 h-5" />
              </motion.div>
            </motion.button>
          </MicroInteraction>
        ))}
      </div>
    </div>
  );
}

// Hero breathwork card for prominent placement
export function BreathworkHeroCard() {
  const navigate = useNavigate();

  return (
    <MicroInteraction type="glow">
      <motion.button
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.01 }}
        onClick={() => navigate('/breathwork')}
        className="relative w-full p-6 rounded-3xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/80 text-secondary-foreground text-left overflow-hidden"
      >
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1] 
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10"
          />
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.15, 0.1] 
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/10"
          />
        </div>

        <div className="relative z-10 flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                Featured
              </span>
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium flex items-center gap-1">
                <Flame className="w-3 h-3" />
                Trending
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-1">Take a breath</h2>
            <p className="text-white/80 text-sm">
              5 minute guided session to reset your day
            </p>
          </div>

          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"
          >
            <Wind className="w-8 h-8" />
          </motion.div>
        </div>

        {/* Progress hint */}
        <div className="relative z-10 mt-4 pt-4 border-t border-white/20">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">Today's streak</span>
            <div className="flex items-center gap-1">
              <Flame className="w-4 h-4 text-primary" />
              <span className="font-semibold">3 days</span>
            </div>
          </div>
        </div>
      </motion.button>
    </MicroInteraction>
  );
}

// Duolingo-style daily streak widget
export function DailyStreakWidget({ streak = 0 }: { streak?: number }) {
  const hasStreak = streak > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20"
    >
      <motion.div
        animate={hasStreak ? { 
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        } : {}}
        transition={{ duration: 2, repeat: Infinity }}
        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          hasStreak 
            ? 'bg-gradient-to-br from-primary to-primary/80' 
            : 'bg-muted/50'
        }`}
      >
        <Flame className={`w-6 h-6 ${hasStreak ? 'text-white' : 'text-muted-foreground'}`} />
      </motion.div>

      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-foreground">{streak}</span>
          <span className="text-sm text-muted-foreground">day streak</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {hasStreak 
            ? "Keep it going! Check in daily." 
            : "Start your streak today!"}
        </p>
      </div>

      {hasStreak && streak >= 7 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium"
        >
          🔥 On fire!
        </motion.div>
      )}
    </motion.div>
  );
}
